import AppBarChart from "@/components/Common/Charts/AppBarChart";
import LazyTooltip from "@/components/Common/LazyTooltip";
import FlagComponent from "@/components/ui/FlagComponent";
import type {GraphDataSerializer} from "@/generated/DjangoTypes";
import useCharts from "@/hooks/useCharts";
import useCountries from "@/hooks/useCountries";
import clsxm from "@/lib/clsxm";
import type {TCampaignType} from "@/store/types";
import {useId, type FC} from "react";
import {useTranslation} from "react-i18next";

const TopFollowerCountry: FC<{
  className?: string;
  prioritizedCountries?: string[];
  graphs: GraphDataSerializer;
  influencerVerificationCategory: TCampaignType;
  textClassName?: string;
}> = ({className, prioritizedCountries, graphs, influencerVerificationCategory, textClassName}) => {
  const {t} = useTranslation("influencer_profile");

  const {getCountryCodeByEnLabel, getCountryCodeByEnLabelOrUndefined} = useCountries();
  const {countriesData, citiesData} = useCharts(graphs, influencerVerificationCategory);

  const graphCountries =
    (influencerVerificationCategory === "tiktok" ? graphs.tiktok_countries : graphs.countries) ?? [];

  const prioritizedMatch = prioritizedCountries?.length
    ? graphCountries.filter(c => prioritizedCountries.includes(c.label)).sort((a, b) => b.value - a.value)[0]
    : undefined;

  const country = prioritizedMatch ?? graphCountries[0];
  const topCountryStatistics = {
    ...country,
    code: country?.label ? getCountryCodeByEnLabel(country.label) : undefined,
  };

  const id = useId();

  if (!topCountryStatistics.code) {
    return null;
  }

  return (
    <>
      <div data-tooltip-id={`top-follower-country-${id}`} className={clsxm("flex items-center gap-1", className)}>
        <FlagComponent
          className="outline-neutral-grey-light-dark size-3 rounded-full outline [&_svg]:scale-[1.5]"
          country={topCountryStatistics.code}
        />
        <span
          className={clsxm(
            "text-neutral-black h-fit text-xs font-semibold whitespace-nowrap",
            textClassName,
          )}>{`${topCountryStatistics.value}%`}</span>
      </div>
      <LazyTooltip
        id={`top-follower-country-${id}`}
        clickable
        place="bottom"
        variant="light"
        opacity={1}
        delayShow={200}
        className="z-100 max-w-72 cursor-default p-0 shadow-md"
        classNameArrow="shadow-md">
        <div className="flex flex-col gap-3 px-4 py-2" onClick={e => e.stopPropagation()}>
          <AppBarChart
            chartTitle={t("countries_reached")}
            chartData={countriesData}
            formatLabel={getCountryCodeByEnLabelOrUndefined}
          />
          <AppBarChart chartTitle={t("top_cities")} chartData={citiesData} />
        </div>
      </LazyTooltip>
    </>
  );
};

export default TopFollowerCountry;
